import React from 'react';
import { Button, Modal, NavDropdown } from 'react-bootstrap';
import User from '../models/User';
import { isStringNotEmpty } from '../utils/objectUtils';
import TableSearchBar from '../components/Table/TableSearchBar';
import TableEditable from '../components/Table/TableEditable';
import { useUsers } from '../redux/ducks/users';
import { toFormattedPhoneNumber, toFormattedString, toYesNo } from '../utils/stringFormatter';
import { useAuth } from '../redux/ducks/auth';
import { resetFunnel } from '../redux/ducks/funnel';
import { clearCart } from '../redux/ducks/cart';
import { resetAnalytics } from '../redux/ducks/analytics';
import { useSearchParams } from 'react-router';
import { useDispatch } from 'react-redux';
import LoadingWrapper from '../components/LoadingWrapper';

export const RoleSwitcherModalMenuItem = () => {
    const [showModal, setShowModal] = React.useState(false);

    //#endregion

    return (
        <>
            <NavDropdown.Item onClick={() => { setShowModal(true); }}>Impersonate a Patient</NavDropdown.Item>
            {showModal ? (
                <RoleSwitcherModal
                    showModal={showModal}
                    setShowModal={setShowModal}
                />
            ) : null}
        </>
    );
};

const RoleSwitcherModal = ({ showModal, setShowModal }) => {
    //#region useAuth

    const { impersonateUser, isAuthLoading, authError, currentAuthUserId } = useAuth();
    //#endregion
    //#region useSearchParams

    const [searchParams, setSearchParams] = useSearchParams();

    //#endregion
    //#region useDispatch

    const dispatch = useDispatch();

    //#endregion
    //#region useUsers

    const { reps } = useUsers();

    //#endregion
    //#region useRefs

    // We need to keep the table from resetting the pageIndex when we Update data. So we can keep track of that flag with a ref.
    const skipResetRef = React.useRef(false);

    //#endregion
    //#region useStates

    const [data, setData] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);

    //#endregion
    //#region modal controls

    const handleCloseModal = () => {
        setShowModal(false);
    };

    //#endregion
    //#region change user methods

    /**
     * Switching who you are acting as is a session change, not just a profile load.
     *
     * `impersonateUser` is `getProfile(userId)`, which replaces the auth duck's copy of the customer and
     * nothing else - so the funnel, cart and analytics ducks kept the PREVIOUS patient's state. Their
     * goals answers, their cart and their `affiliateInfo` were then written onto the next patient's lead,
     * which meant one patient's conversion could pay another's affiliate. The stale `?lid` in the URL is
     * dropped for the same reason: it points at the previous patient's lead.
     *
     * Cleared before the profile load, so nothing can read the old state in between, and on the way back
     * too - returning to yourself is the same kind of switch.
     */
    const clearImpersonationState = () => {
        dispatch(resetFunnel());
        dispatch(clearCart());
        dispatch(resetAnalytics());

        if (searchParams.has('lid')) {
            searchParams.delete('lid');
            setSearchParams(searchParams, { replace: true });
        }
    };

    const onSwitchRole = async (val) => {
        clearImpersonationState();
        await impersonateUser(val);
        setShowModal(false);
    };

    const switchBackToMe = async () => {
        clearImpersonationState();
        await impersonateUser(currentAuthUserId);
        setShowModal(false);
    };

    //#endregion
    //#region search methods

    const searchUsers = async (data) => {
        console.log(data);
        if (isStringNotEmpty(data.firstName) || isStringNotEmpty(data.lastName) || isStringNotEmpty(data.email) || isStringNotEmpty(data.phone) || isStringNotEmpty(data.userId)) {
            // We also turn on the flag to not reset the page
            skipResetRef.current = true;
            setIsLoading(true);
            const list = await User.getByFirstNameAndLastNameAndEmailAndPhoneAndPatientId(data.firstName, data.lastName, data.email, data.phone, null, data.userId);
            setData(list);
            setIsLoading(false);
        }
    };

    //#endregion
    //#region useMemos

    const columns = React.useMemo(
        () => [
            {
                header: '',
                accessorKey: 'userId',
                id: 'actions',
                enableSorting: false,
                enableColumnFilter: false,
                cell: ({ getValue }) => {
                    return <Button onClick={() => { onSwitchRole(getValue()); }}>Impersonate User</Button>;
                },
            },
            {
                header: 'Patient ID',
                accessorKey: 'patientId',
                id: 'patientId',
                enableColumnFilter: false,
                enableSorting: false,
            },
            {
                header: 'First Name',
                accessorKey: 'firstName',
                id: 'firstName',
            },
            {
                header: 'Middle Name',
                accessorKey: 'middleName',
                id: 'middleName',
                enableSorting: false,
                enableColumnFilter: false,
            },
            {
                header: 'Last Name',
                accessorKey: 'lastName',
                id: 'lastName',
            },
            {
                header: 'Suffix',
                accessorKey: 'customer.suffix',
                id: 'suffix',
                enableSorting: false,
                enableColumnFilter: false,
            },
            {
                header: 'DOB',
                accessorKey: 'dateOfBirth',
                id: 'dateOfBirth',
                enableSorting: false,
                enableColumnFilter: false,
            },
            {
                header: 'Gender',
                accessorKey: 'gender',
                id: 'gender',
                cell: ({ row, getValue }) => { return toFormattedString('Gender', getValue()); },
                enableSorting: false,
                enableColumnFilter: false,
            },
            {
                header: 'Email',
                accessorKey: 'email',
                id: 'email',
            },
            {
                header: 'Phone',
                accessorKey: 'phone',
                id: 'phone',
                enableSorting: false,
                enableColumnFilter: false,
                cell: ({ getValue }) => { return toFormattedPhoneNumber(getValue()); },
            },
            {
                header: 'Is Customer Service Rep',
                accessorFn: row => toYesNo(row.isRep),
                id: 'isRep',
            },
            {
                header: 'Is Help Staff',
                accessorFn: row => toYesNo(row.isHelpStaff),
                id: 'isHelpStaff',
            },
            {
                header: 'Is Admin',
                accessorFn: row => toYesNo(row.isAdmin),
                id: 'isAdmin',
            },
            {
                header: 'Is Super Admin',
                accessorFn: row => toYesNo(row.isSuperAdmin),
                id: 'isSuperAdmin',
            },
            {
                header: 'Is Owner',
                accessorFn: row => toYesNo(row.isOwner),
                id: 'isOwner',
            },
            {
                header: 'Is Staff Manager',
                accessorFn: row => toYesNo(row.isStaffManager),
                id: 'isStaffManager',
            },
            {
                header: 'Is Marketing Manager',
                accessorFn: row => toYesNo(row.isMarketingManager),
                id: 'isMarketingManager',
            },
            {
                header: 'Is Friends and Family',
                accessorFn: row => toYesNo(row.isFriendsAndFamily),
                id: 'isFriendsAndFamily',
            },
            {
                header: 'User ID',
                accessorKey: 'userId',
                id: 'userId',
                enableColumnFilter: false,
                enableSorting: false,
            },
        ],
        [reps]
    );

    //#endregion

    return (
        <>
            <Modal
                show={showModal}
                onHide={() => { setShowModal(false); }}
                backdrop="static"
                scrollable
                fullscreen
            >
                <Modal.Header closeButton><Modal.Title>Impersonate User</Modal.Title></Modal.Header>
                <Modal.Body>
                    <LoadingWrapper isLoading={isLoading} />
                    <TableSearchBar key="impersonate-search" title="Search Criteria" formId="impersonate-search" search={searchUsers} isLoading={isLoading || isAuthLoading} showUserId />
                    <TableEditable
                        columns={columns}
                        data={data}
                        skipReset={skipResetRef.current}
                        singularEntityName="Patient"
                        pluralEntityName="Patients"
                        isLoading={isLoading || isAuthLoading}
                        error={authError}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => { switchBackToMe(); }}>Switch back to Me</Button>
                    <Button variant="secondary" onClick={handleCloseModal} disabled={isLoading || isAuthLoading}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default RoleSwitcherModal;
